import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Play,
  Star,
  Clock,
  Shield,
  Zap,
} from "lucide-react";
import SecondaryNavbar from "../../components/common/SecondaryNavbar";
import servicesData from "../../context/Services.json";
import SecondaryHero from "../../components/common/SecondaryHero";

const Services = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeService, setActiveService] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Find the category and set active service
  useEffect(() => {
    if (categoryId) {
      const category = servicesData.services.find(
        (service) => service.categoryId === categoryId
      );
      if (category) {
        setSelectedCategory(category);
        setActiveService(category.services[0]);
        setCurrentImageIndex(0);
      }
    }
  }, [categoryId]);

  const navigateToCategory = (categoryId) => {
    navigate(`/services/${categoryId}`);
  };

  const handleServiceTabClick = (service) => {
    setActiveService(service);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (activeService && activeService.images) {
      setCurrentImageIndex((prev) =>
        prev === activeService.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (activeService && activeService.images) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? activeService.images.length - 1 : prev - 1
      );
    }
  };

  if (!selectedCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-32 flex items-center justify-center">
        <SecondaryNavbar />
        <div className="text-center">
          <div className="w-24 h-24 bg-gradient-to-r from-brandBlue to-brandGreen rounded-full flex items-center justify-center mx-auto mb-6">
            <Zap className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Service Not Found
          </h2>
          <p className="text-gray-600 text-lg max-w-md mx-auto">
            The service category you're looking for doesn't exist or has been
            moved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
      <SecondaryNavbar />

      <SecondaryHero
        pageName="Our Services"
        pageHead1={selectedCategory.category}
        pageHead2=""
        pageHead3=""
        pageHead4=""
        pageDesc={selectedCategory.description}
        bgImgUrl="https://rare-gallery.com/mocahbig/73012-Nature-GreeneryWaterfall-4k-Ultra-HD-Wallpaper.jpg"
      />

      {/* Main Content */}
      <section className="py-6 md:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Services Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-3 justify-center">
              {selectedCategory.services.map((service, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleServiceTabClick(service)}
                  className={`px-6 py-4 rounded-2xl font-semibold transition-all duration-300 border-2 relative overflow-hidden group ${
                    activeService?.name === service.name
                      ? "bg-white border-brandBlue text-brandBlue shadow-xl"
                      : "bg-white border-gray-200 text-gray-700 hover:border-brandBlue/50"
                  }`}
                >
                  <span className="relative z-10">{service.name}</span>
                  {activeService?.name === service.name && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gradient-to-r from-brandBlue/5 to-brandGreen/5 rounded-2xl"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Active Service Content */}
          <AnimatePresence mode="wait">
            {activeService && (
              <motion.div
                key={activeService.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="grid lg:grid-cols-2 gap-12 items-start"
              >
                {/* Enhanced Image Gallery */}
                <div className="space-y-6">
                  <div className="relative group">
                    <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                      <motion.img
                        key={currentImageIndex}
                        src={activeService.images[currentImageIndex]}
                        alt={activeService.name}
                        className="w-full h-80 lg:h-96 object-cover"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4 }}
                      />

                      {/* Image Navigation */}
                      {activeService.images.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 shadow-lg opacity-0 group-hover:opacity-100 backdrop-blur-sm"
                          >
                            <ChevronLeft className="w-6 h-6 text-gray-700" />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all duration-300 shadow-lg opacity-0 group-hover:opacity-100 backdrop-blur-sm"
                          >
                            <ChevronRight className="w-6 h-6 text-gray-700" />
                          </button>

                          {/* Image Indicators */}
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                            {activeService.images.map((_, index) => (
                              <button
                                key={index}
                                onClick={() => setCurrentImageIndex(index)}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                  index === currentImageIndex
                                    ? "bg-white w-6"
                                    : "bg-white/50"
                                }`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Service Highlights */}
                  <div className="grid grid-cols-3 gap-4 pt-4">
                    <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                      <Clock className="w-8 h-8 text-brandBlue mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Fast Delivery</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                      <Shield className="w-8 h-8 text-brandGreen mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Secure</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                      <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">Premium</div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Service Details */}
                <div className="space-y-8">
                  <div>
                    <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 font-lexend">
                      {activeService.name}
                    </h2>
                    <p className="text-xl text-gray-600 leading-relaxed">
                      {activeService.description}
                    </p>
                  </div>

                  {/* Features Grid with Icons */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <Zap className="w-6 h-6 text-brandBlue" />
                      Key Features
                    </h3>
                    <div className="grid gap-4">
                      {activeService.features.map((feature, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 group"
                        >
                          <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                          <span className="text-gray-700 font-medium">
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* All Services Gallery */}
      <section className="py-6 md:py-8 bg-white/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 font-lexend">
              Our {selectedCategory.category} Portfolio
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Visual showcase of our exceptional work and service delivery
            </p>
          </motion.div>

          {/* Alternative: Grid Layout with Equal Height */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {selectedCategory.services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                onClick={() => handleServiceTabClick(service)}
                className="group cursor-pointer"
              >
                <div className="relative aspect-square rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500">
                  <img
                    src={service.images?.[0] || "/api/placeholder/400/400"}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <h3 className="text-white font-bold text-lg text-center">
                      {service.name}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Categories */}
      <section className="py-6 md:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 font-lexend">
              Explore More Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover our complete suite of professional solutions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesData.services.map((category, index) => (
              <motion.div
                key={category.categoryId}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                onClick={() => navigateToCategory(category.categoryId)}
                className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 relative overflow-hidden group ${
                  selectedCategory.categoryId === category.categoryId
                    ? "border-brandBlue"
                    : "border-transparent"
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brandBlue/5 to-brandGreen/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-brandBlue to-brandGreen rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <img
                      src={category.icon}
                      alt=""
                      className="w-8 h-8 object-contain filter brightness-0 invert"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-lexend group-hover:text-brandBlue transition-colors">
                    {category.category}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {category.description.substring(0, 80)}...
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-4xl w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="aspect-video bg-gray-200 rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <Play className="w-16 h-16 text-brandBlue mx-auto mb-4" />
                  <p className="text-gray-600">Service Overview Video</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Services;
